/**
 * QAILandingPage (F1) - v5.0 Complete Redesign
 * Marketing landing page for Q AI standalone product
 *
 * Route: /q-ai (public, no auth required)
 * Target: Retail MT5 traders who are NOT existing QTradeView users
 *
 * v5.0 Major Improvements (Based on Q_AI_USER_JOURNEY.md):
 * - New Hero section with trust badges
 * - Stats bar with key metrics
 * - "What is Q?" explainer section
 * - Enhanced feature cards (Signal Board, Q Chat, Quick Actions, Profile, Alerts)
 * - Simplified 3-step How It Works
 * - Testimonials section
 * - FAQ with expandable sections
 * - Updated pricing (Free/Pro/Enterprise)
 * - Professional footer with full navigation
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import InsightsIcon from '@mui/icons-material/Insights';
import ChatIcon from '@mui/icons-material/Chat';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LinkIcon from '@mui/icons-material/Link';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import QAILogo from '../../Components/QAI/QAILogo';

// Discord icon as SVG
const DiscordIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
  // Primary Colors
  primary: '#27B56D',
  primaryLight: '#22C55E',
  primaryDark: '#16A34A',
  accent: '#3B82F6',

  // Background & Surface
  background: '#0A0A0F',
  surface: '#12121A',
  surfaceAlt: '#0D0D14',

  // Text Hierarchy
  text: '#FFFFFF',
  textSecondary: '#E2E8F0',
  textMuted: '#94A3B8',
  textDim: '#64748B',

  // Borders
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.12)',

  // Effects
  glow: 'rgba(39, 181, 109, 0.4)'
};

// ═══════════════════════════════════════════════════════════════════════════════
// TYPOGRAPHY SYSTEM - Institutional Grade
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// TYPOGRAPHY SCALE - Polished & Refined (v2)
// ═══════════════════════════════════════════════════════════════════════════════
//
// HEADER:     Logo 26-30px | Nav 14px | Buttons 13-14px
// HERO:       Wordmark 64-144px | Tagline 22-32px | Body 16-18px | Social 13-14px
// SECTIONS:   Label 12px | Title 28-40px | Subtitle 15-16px | Body 13-14px
// CARDS:      Title 16-18px | Subtitle 10px | Body 13px
// PRICING:    Tokens 18-22px | Price 22-28px | Features 12px | Button 13px
// FAQ:        Question 14-15px | Answer 13-14px
// FOOTER:     Logo 24px | Section 13px | Links 12px | Copyright 11px
//
const TYPOGRAPHY = {
  // Font Weights
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800
  },
  // Font Sizes - Refined Scale
  size: {
    xxs: '0.625rem',    // 10px - micro labels
    xs: '0.6875rem',    // 11px - tiny text, copyright
    sm: '0.75rem',      // 12px - labels, footer links
    base: '0.8125rem',  // 13px - small buttons, descriptions
    md: '0.875rem',     // 14px - nav, links, body small
    lg: '0.9375rem',    // 15px - body text, FAQ questions
    xl: '1rem',         // 16px - body large, card titles
    '2xl': '1.125rem',  // 18px - step titles
    '3xl': '1.375rem',  // 22px - tagline mobile
    '4xl': '1.5rem',    // 24px - footer logo
    '5xl': '1.75rem',   // 28px - section titles mobile
    '6xl': '2rem',      // 32px - section titles tablet
    '7xl': '2.5rem',    // 40px - section titles desktop
    '8xl': '4rem',      // 64px - hero mobile
    '9xl': '5.5rem',    // 88px - hero tablet
    '10xl': '7rem',     // 112px - hero desktop
    '11xl': '9rem'      // 144px - hero xl
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// SPACING SYSTEM - 8px Grid
// ═══════════════════════════════════════════════════════════════════════════════

const SPACING = {
  // Base unit = 8px (MUI spacing(1) = 8px)
  headerHeight: { xs: 64, md: 72 },  // 8 * 8 = 64px, 9 * 8 = 72px
  sectionPadding: { xs: 6, md: 10 }, // 48px mobile, 80px desktop
  containerPadding: { xs: 2, md: 3 }, // 16px mobile, 24px desktop
  cardPadding: { xs: 2.5, md: 3 },   // 20px mobile, 24px desktop
  gap: {
    xs: 1,    // 8px
    sm: 1.5,  // 12px
    md: 2,    // 16px
    lg: 3,    // 24px
    xl: 4,    // 32px
    '2xl': 6  // 48px
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// STATS BAR DATA
// ═══════════════════════════════════════════════════════════════════════════════

const STATS = [
  { label: 'Response Time', value: '< 2', unit: 'sec' },
  { label: 'Trades Analyzed', value: '2M', unit: '+' },
  { label: 'Cost per Query', value: '< $0.01', unit: '' },
  { label: 'Uptime', value: '99.9', unit: '%' }
];

// ═══════════════════════════════════════════════════════════════════════════════
// TRUST BADGES
// ═══════════════════════════════════════════════════════════════════════════════

const TRUST_BADGES = [
  { icon: <SecurityIcon />, text: 'Bank-Grade Encryption' },
  { icon: <SpeedIcon />, text: 'Sub-Second Response' },
  { icon: <InsightsIcon />, text: 'Data Analysis Only' }
];

// ═══════════════════════════════════════════════════════════════════════════════
// KEY FEATURES (Matching Q AI Product Content)
// ═══════════════════════════════════════════════════════════════════════════════

const KEY_FEATURES = [
  {
    icon: <TrendingUpIcon sx={{ fontSize: 36 }} />,
    title: 'Performance Tracking',
    subtitle: 'Every Trade Logged',
    description: 'Every trade you take gets logged, categorized, and broken down. Win rates, average hold times, which setups actually work for you — it\'s all there.',
    color: BRAND.primary
  },
  {
    icon: <ChatIcon sx={{ fontSize: 36 }} />,
    title: 'Pre-Computed Market Data',
    subtitle: 'Historical Pattern Analysis',
    description: 'Q pulls in processed market statistics so you can see how your decisions stack up against historical patterns. No live feeds, no execution — just data you can learn from.',
    color: '#3B82F6'
  },
  {
    icon: <TouchAppIcon sx={{ fontSize: 36 }} />,
    title: 'Pattern Recognition',
    subtitle: 'Spot Your Mistakes Early',
    description: 'Most traders repeat the same mistakes for years. Q spots those patterns early and shows you what\'s working (and what isn\'t).',
    color: '#8B5CF6'
  },
  {
    icon: <PersonIcon sx={{ fontSize: 36 }} />,
    title: 'Personal Trading Profile',
    subtitle: 'Q Analyzes Your History',
    description: 'Connect your MT5 account. Q imports your entire trading history and builds a data profile—win rates by symbol, best sessions, patterns to watch.',
    color: '#F59E0B'
  },
  {
    icon: <NotificationsActiveIcon sx={{ fontSize: 36 }} />,
    title: 'Q Chat Interface',
    subtitle: 'Ask Questions About Your Data',
    description: 'Chat with Q about your own trading history. Ask questions, review patterns, understand your statistics — all through a simple conversation.',
    color: '#EF4444'
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// HOW IT WORKS - 3 STEPS
// ═══════════════════════════════════════════════════════════════════════════════

const HOW_IT_WORKS = [
  {
    step: 1,
    title: 'Sign Up',
    subtitle: 'Create Your Account',
    description: 'Create your Q.AI account with Google. Free 50 tokens included—no credit card required.',
    time: '30 seconds',
    icon: <PersonIcon sx={{ fontSize: 40 }} />
  },
  {
    step: 2,
    title: 'Install',
    subtitle: 'Get QBridge',
    description: 'Download QBridge and run the installer. Next → Next → Finish. Auto-detects MT5 and installs Q.AI Panel EA for you.',
    time: '2 minutes',
    icon: <LinkIcon sx={{ fontSize: 40 }} />
  },
  {
    step: 3,
    title: 'Start Asking',
    subtitle: 'Q.AI on Your Chart',
    description: 'Open MT5, drag Q.AI Panel onto any chart. Ask about YOUR trades, patterns, and performance. That\'s it!',
    time: 'Instant',
    icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// TESTIMONIALS
// ═══════════════════════════════════════════════════════════════════════════════

const TESTIMONIALS = [
  {
    quote: "Q found patterns in my trading I never noticed. I realized my Friday trades were consistently weaker—useful insight.",
    author: "Marcus T.",
    role: "Forex Trader"
  },
  {
    quote: "Finally, an analytics tool that actually organizes my trading data. Clear dashboards, no fluff.",
    author: "Sarah K.",
    role: "Prop Firm Trader"
  },
  {
    quote: "The dashboard breakdown helped me understand where my approach was inconsistent. Great for self-review.",
    author: "James L.",
    role: "Swing Trader"
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// FAQ DATA
// ═══════════════════════════════════════════════════════════════════════════════

const FAQ_DATA = [
  {
    question: "What makes Q different from ChatGPT or other AI tools?",
    answer: "Q.AI is connected to YOUR MT5 trading data. Unlike generic AI, Q analyzes your actual trades, knows your win rate, your patterns, your weak spots. It's data intelligence, not generic answers."
  },
  {
    question: "How do I add Q.AI to my MT5 chart?",
    answer: "1) Sign up for free, 2) Download and run QBridge installer (Next → Next → Finish), 3) Open MT5, drag Q.AI Panel from Navigator onto any chart. That's it! QBridge auto-detects MT5 and installs the panel for you."
  },
  {
    question: "What is QBridge?",
    answer: "QBridge is our secure desktop connector that links your MT5 to Q.AI. It runs in your system tray, auto-detects MT5, and keeps your trading data synced. Your raw data stays on your computer—only anonymized analytics are processed."
  },
  {
    question: "QBridge not connecting—what should I do?",
    answer: "1) Check if QBridge is running (green Q icon in system tray), 2) Make sure MT5 is open, 3) Try restarting QBridge from Start menu. If still stuck, restart your computer and try again. QBridge auto-starts with Windows."
  },
  {
    question: "How does the token system work?",
    answer: "Simple pay-per-use: 50 FREE tokens to start (no card required). Q Ask = 5-10 tokens, Q Analysis = 15 tokens. Tokens never expire. Top up anytime starting at $4.99."
  },
  {
    question: "Is my trading data secure?",
    answer: "Yes. QBridge runs locally—your raw data never leaves your machine. We use bank-grade encryption (TLS 1.3, AES-256). Q.AI only receives anonymized analytics. You can delete all data anytime."
  },
  {
    question: "Does Q.AI give trading signals or financial advice?",
    answer: "No. Q.AI is a data intelligence tool, not a signal service. We analyze YOUR patterns to help you understand your performance—we don't tell you what to trade. All decisions are yours."
  },
  {
    question: "Does Q.AI execute trades or access my funds?",
    answer: "Never. Q.AI only reads your closed trade history. We have no access to your funds, passwords, or live positions. We don't execute trades on your behalf."
  },
  {
    question: "What brokers does Q.AI support?",
    answer: "Q.AI works with any MT5 broker worldwide—PMEX, ICMarkets, Exness, OANDA, and more. If your broker offers MT5, Q.AI works with it."
  },
  {
    question: "Who built Q.AI?",
    answer: "Q.AI is developed by QFORGEX (Private) Limited, a Pakistani fintech company specializing in trading technology and data analytics solutions."
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// TOKEN COSTS - Architecture Aligned
// ═══════════════════════════════════════════════════════════════════════════════

const TOKEN_COSTS = [
  { action: 'Q Ask (simple)', cost: 5 },
  { action: 'Q Ask (detailed)', cost: 10 },
  { action: 'Q Analysis', cost: 15 },
  { action: 'QBridge Sync', cost: '2/100 trades' }
];

// ═══════════════════════════════════════════════════════════════════════════════
// PRICING TIERS - Token-Based (Architecture Aligned)
// ═══════════════════════════════════════════════════════════════════════════════

const PRICING_TIERS = [
  {
    name: 'Free Trial',
    tokens: 50,
    price: 0,
    period: '',
    description: 'Start exploring Q',
    features: [
      '50 free tokens to start',
      'Full access to all features',
      'Q Dashboard (all symbols)',
      'Q Chat interface',
      'No credit card required'
    ],
    cta: 'Start Free',
    popular: false,
    isTokenPack: false
  },
  {
    name: 'Starter',
    tokens: 500,
    price: 10,
    period: 'one-time',
    description: 'Top up as needed',
    features: [
      '500 tokens',
      '~50 Q Ask queries',
      '~33 Q Analysis reports',
      'Never expires',
      'Pay only for what you use'
    ],
    cta: 'Buy Tokens',
    popular: false,
    isTokenPack: true
  },
  {
    name: 'Standard',
    tokens: 1500,
    price: 25,
    period: 'one-time',
    description: 'Best value for active users',
    features: [
      '1,500 tokens',
      '~150 Q Ask queries',
      '~100 Q Analysis reports',
      'Save 17% vs Starter',
      'Priority support'
    ],
    cta: 'Buy Tokens',
    popular: true,
    isTokenPack: true
  },
  {
    name: 'Pro',
    tokens: 5000,
    price: 50,
    period: 'one-time',
    description: 'For power users',
    features: [
      '5,000 tokens',
      '~500 Q Ask queries',
      '~333 Q Analysis reports',
      'Save 50% vs Starter',
      'Priority support'
    ],
    cta: 'Buy Tokens',
    popular: false,
    isTokenPack: true
  },
  {
    name: 'Unlimited',
    tokens: '∞',
    price: 99,
    period: '/month',
    description: 'No limits, no worries',
    features: [
      'Unlimited tokens',
      'Unlimited Q Chat',
      'Unlimited Q Analysis',
      'All features unlocked',
      'Priority 24/7 support'
    ],
    cta: 'Go Unlimited',
    popular: false,
    isTokenPack: false
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// FOOTER LINKS
// ═══════════════════════════════════════════════════════════════════════════════

const FOOTER_LINKS = {
  product: [
    { label: 'Q Dashboard', id: 'features' },
    { label: 'Q Chat', id: 'features' },
    { label: 'Q Analysis', id: 'features' },
    { label: 'Token Pricing', id: 'pricing' }
  ],
  resources: [
    { label: 'QBridge Download', url: '/qbridge', hasInstallerNote: true },
    { label: 'Documentation', url: '/docs' },
    { label: 'Discord Community', url: 'https://discord.gg/quantx' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Support', url: '/support' }
  ],
  helpCenter: [
    { label: 'Contact Us', url: '/contact' },
    { label: 'System Status', url: '/status' },
    { label: 'Report Bug', url: '/feedback' }
  ],
  legal: [
    { label: 'Privacy Policy', url: '/privacy' },
    { label: 'Terms of Service', url: '/terms' },
    { label: 'Cookie Policy', url: '/cookies' },
    { label: 'Restricted Regions', url: '/restrictions' }
  ]
};

const SOCIAL_LINKS = [
  { icon: <TwitterIcon />, url: '#', label: 'Twitter/X' },
  { icon: <DiscordIcon />, url: '#', label: 'Discord' },
  { icon: <LinkedInIcon />, url: '#', label: 'LinkedIn' },
  { icon: <YouTubeIcon />, url: '#', label: 'YouTube' }
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const QAILandingPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Navigation links for both desktop and mobile
  const NAV_LINKS = [
    { label: 'Features', id: 'features' },
    { label: 'How It Works', id: 'how-it-works' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'FAQ', id: 'faq' }
  ];

  // Smooth scroll to section with header offset
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 60; // Reduced offset for better section visibility
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const handleFaqChange = (panel) => (_, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <Box sx={{ backgroundColor: BRAND.background, minHeight: '100vh' }}>
      {/* ═══════════════════════════════════════════════════════════════════════
          HEADER - Bolt.new Inspired Design
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'rgba(10, 10, 15, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${BRAND.border}`
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: { xs: 56, md: 64 }, // Compact header
              px: { xs: 0.5, md: 1 }
            }}
          >
            {/* Left: Wordmark + Tagline - Premium Branding */}
            {/* Header Logo - Proportional sizing (v4.0) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, md: 1.25 },
                cursor: 'pointer'
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              {/* Responsive Logo: md on mobile, lg on desktop */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <QAILogo size="md" showText />
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-start' }}>
                <QAILogo size="lg" showText />
                {/* Tagline - under Q.ai on desktop */}
                <Typography
                  sx={{
                    color: BRAND.textMuted,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                    mt: 0.25,
                    ml: '54px' // Align with text after logo icon
                  }}
                >
                  AI-Powered Analytic System
                </Typography>
              </Box>
            </Box>

            {/* Center: Navigation Links - Desktop Only */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: { md: 4, lg: 5 } // 32-40px spacing - Bolt.new style
              }}
            >
              {NAV_LINKS.map((link) => (
                <Typography
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  sx={{
                    color: BRAND.textMuted,
                    fontSize: '1.0625rem', // 17px - Bolt.new bold nav
                    fontWeight: 600, // Bolder for better visibility
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: BRAND.text
                    }
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>

            {/* Right: Social + Auth Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1.5 } }}>
              {/* Desktop: Social Icons like Bolt */}
              <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5, mr: 1 }}>
                {[
                  { icon: <DiscordIcon />, label: 'Discord' },
                  { icon: <LinkedInIcon sx={{ fontSize: 20 }} />, label: 'LinkedIn' },
                  { icon: <TwitterIcon sx={{ fontSize: 20 }} />, label: 'X' }
                ].map((social) => (
                  <IconButton
                    key={social.label}
                    size="small"
                    sx={{
                      color: BRAND.textDim,
                      p: 1,
                      '&:hover': {
                        color: BRAND.text,
                        backgroundColor: 'transparent'
                      }
                    }}
                    aria-label={social.label}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>

              {/* Desktop: Sign in Button */}
              <Button
                onClick={() => navigate('/q-ai/login')}
                sx={{
                  color: BRAND.textMuted,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem', // 16px - Bolt.new style
                  px: 2.5,
                  py: 1,
                  display: { xs: 'none', md: 'flex' },
                  borderRadius: '10px',
                  minHeight: 44,
                  '&:hover': {
                    color: BRAND.text,
                    backgroundColor: 'transparent'
                  }
                }}
              >
                Sign in
              </Button>

              {/* Desktop: Get Started Button - Premium CTA */}
              <Button
                onClick={() => navigate('/q-ai/signup')}
                variant="contained"
                sx={{
                  background: BRAND.primary,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9375rem', // 15px - refined
                  px: 2.5,
                  py: 1,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  display: { xs: 'none', sm: 'flex' },
                  minHeight: 40,
                  alignItems: 'center',
                  '&:hover': {
                    background: BRAND.primaryLight,
                    boxShadow: `0 4px 16px ${BRAND.glow}`
                  }
                }}
              >
                Get started
              </Button>

              {/* Mobile: Hamburger Menu Button */}
              <IconButton
                onClick={handleMobileMenuToggle}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  color: BRAND.text,
                  p: 1,
                  minWidth: 44,
                  minHeight: 44,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
                aria-label="Open navigation menu"
              >
                <MenuIcon sx={{ fontSize: 26 }} />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          MOBILE NAVIGATION DRAWER
          ═══════════════════════════════════════════════════════════════════════ */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: 320,
            backgroundColor: BRAND.background,
            borderLeft: `1px solid ${BRAND.border}`
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Drawer Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
              pb: 2,
              borderBottom: `1px solid ${BRAND.border}`
            }}
          >
            {/* Brand Block - Proportional to box */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(39, 181, 109, 0.35)'
                }}
              >
                <Typography sx={{ color: '#FFFFFF', fontSize: '1.375rem', fontWeight: 800, fontFamily: "'Inter', sans-serif" }}>
                  Q
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  sx={{
                    color: BRAND.text,
                    fontSize: '1.75rem',
                    fontWeight: 800,
                    letterSpacing: '-0.025em',
                    fontFamily: "'Inter', -apple-system, sans-serif",
                    lineHeight: 1,
                    '& .accent': { color: BRAND.primary, fontWeight: 700 }
                  }}
                >
                  Q<span className="accent">.ai</span>
                </Typography>
                <Typography sx={{ color: BRAND.textMuted, fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', mt: 0.25 }}>
                  AI-Powered Analytic System
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleMobileMenuToggle}
              sx={{
                color: BRAND.textMuted,
                minWidth: 44,
                minHeight: 44,
                '&:hover': { color: BRAND.text }
              }}
              aria-label="Close navigation menu"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation Links */}
          <List sx={{ mb: 3 }}>
            {NAV_LINKS.map((link) => (
              <ListItem key={link.id} disablePadding>
                <ListItemButton
                  onClick={() => scrollToSection(link.id)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: '8px',
                    mb: 0.5,
                    minHeight: 48,
                    '&:hover': {
                      backgroundColor: 'rgba(39, 181, 109, 0.08)'
                    }
                  }}
                >
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{
                      sx: {
                        color: BRAND.textSecondary,
                        fontSize: TYPOGRAPHY.size.base,
                        fontWeight: TYPOGRAPHY.weight.medium
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ borderColor: BRAND.border, mb: 3 }} />

          {/* Auth Buttons in Drawer */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              onClick={() => {
                navigate('/q-ai/login');
                setMobileMenuOpen(false);
              }}
              variant="outlined"
              fullWidth
              sx={{
                borderColor: BRAND.borderLight,
                color: BRAND.text,
                textTransform: 'none',
                fontWeight: TYPOGRAPHY.weight.semibold,
                fontSize: TYPOGRAPHY.size.base,
                py: 1.5,
                borderRadius: '10px',
                minHeight: 48,
                '&:hover': {
                  borderColor: BRAND.primary,
                  backgroundColor: 'rgba(39, 181, 109, 0.08)'
                }
              }}
            >
              Login
            </Button>
            <Button
              onClick={() => {
                navigate('/q-ai/signup');
                setMobileMenuOpen(false);
              }}
              variant="contained"
              fullWidth
              sx={{
                background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
                textTransform: 'none',
                fontWeight: TYPOGRAPHY.weight.bold,
                fontSize: TYPOGRAPHY.size.base,
                py: 1.5,
                borderRadius: '10px',
                minHeight: 48,
                boxShadow: `0 4px 20px ${BRAND.glow}`,
                '&:hover': {
                  boxShadow: `0 6px 30px rgba(39, 181, 109, 0.5)`
                }
              }}
            >
              Get Started
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ═══════════════════════════════════════════════════════════════════════
          HERO SECTION - Compact Spacing
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: { xs: 4, sm: 5, md: 6 },  // 32px, 40px, 48px - REDUCED
          pb: { xs: 4, sm: 5, md: 6 },  // 32px, 40px, 48px - REDUCED
          px: SPACING.containerPadding,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated Background Glow */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '350px', md: '550px' },
            height: { xs: '200px', md: '300px' },
            background: `radial-gradient(ellipse, rgba(39, 181, 109, 0.12) 0%, transparent 60%)`,
            pointerEvents: 'none',
            animation: 'heroGlow 4s ease-in-out infinite'
          }}
        />

        <Container maxWidth="lg" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {/* Announcement Badge - Like Bolt.new */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              mb: 2.5,
              px: 2.5,
              py: 0.75,
              borderRadius: '100px',
              background: 'rgba(39, 181, 109, 0.1)',
              border: `1px solid rgba(39, 181, 109, 0.2)`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'rgba(39, 181, 109, 0.15)',
                borderColor: 'rgba(39, 181, 109, 0.4)'
              }
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18, color: BRAND.primary }} />
            <Typography
              sx={{
                fontSize: '0.9375rem', // 15px - more readable
                fontWeight: 500,
                color: BRAND.textSecondary,
                letterSpacing: '0.01em'
              }}
            >
              50 free tokens to start • No credit card required
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 16, color: BRAND.textMuted }} />
          </Box>

          {/* Large Hero Wordmark - Bolt Style */}
          <Typography
            sx={{
              fontSize: { xs: '3.5rem', sm: '5rem', md: '6rem', lg: '7.5rem' }, // Slightly reduced
              fontWeight: 800,
              letterSpacing: '-0.04em',
              lineHeight: 0.9,
              fontFamily: "'Inter', -apple-system, sans-serif",
              mb: 1.5,
              '& .accent': {
                color: BRAND.primary,
                fontWeight: 600
              }
            }}
          >
            <Box component="span" sx={{ color: BRAND.text }}>Q</Box>
            <Box component="span" className="accent">.ai</Box>
          </Typography>

          {/* Tagline - From Architecture Doc */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.5rem' }, // 24-40px - LARGER
              fontWeight: 500,
              color: BRAND.textSecondary,
              mb: 2,
              letterSpacing: '-0.01em',
              lineHeight: 1.3,
              fontFamily: "'Inter', -apple-system, sans-serif"
            }}
          >
            Ask Q. Understand Your Data.
          </Typography>

          {/* Subheadline - Concise, Impactful */}
          <Typography
            sx={{
              color: '#A3B8C2', // Brighter than BRAND.textMuted for better visibility
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' }, // 18-22px
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: 520,
              mx: 'auto',
              mb: 2
            }}
          >
            AI-powered analytics that organize your trading history into actionable insights.
          </Typography>

          {/* Social Proof */}
          <Typography
            sx={{
              color: BRAND.textMuted, // Brighter than textDim for better visibility
              fontSize: { xs: '1rem', md: '1.0625rem' }, // 16-17px
              fontWeight: 500,
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <Box
              component="span"
              sx={{
                color: BRAND.primary,
                fontWeight: 600
              }}
            >
              2,400+
            </Box>
            traders already using Q for analytics
          </Typography>

          {/* CTA Buttons - Compact */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 1.5 },
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2.5,
              width: '100%'
            }}
          >
            <Button
              onClick={() => navigate('/q-ai/signup')}
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '1.0625rem', md: '1.125rem' }, // 17-18px - LARGER
                minWidth: { xs: '100%', sm: 180 },
                maxWidth: { xs: '280px', sm: 'none' },
                px: 2.5,
                py: 1,
                borderRadius: '8px',
                boxShadow: `0 4px 16px ${BRAND.glow}`,
                minHeight: 44,
                '&:hover': {
                  background: `linear-gradient(135deg, ${BRAND.primaryLight} 0%, ${BRAND.primaryDark} 100%)`,
                  boxShadow: `0 6px 20px rgba(39, 181, 109, 0.5)`,
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: BRAND.borderLight,
                color: BRAND.text,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '1.0625rem', md: '1.125rem' }, // 17-18px - LARGER
                minWidth: { xs: '100%', sm: 180 },
                maxWidth: { xs: '280px', sm: 'none' },
                px: 2.5,
                py: 1,
                borderRadius: '8px',
                borderWidth: '1.5px',
                minHeight: 44,
                '&:hover': {
                  borderColor: BRAND.primary,
                  backgroundColor: 'rgba(39, 181, 109, 0.08)',
                  borderWidth: '1.5px'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Watch Demo
            </Button>
          </Box>

          {/* Trust Badges - Compact */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5, md: 2 }
            }}
          >
            {TRUST_BADGES.map((badge, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: { xs: 1.5, md: 2 },
                  py: { xs: 0.5, md: 0.75 },
                  borderRadius: '100px',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  border: `1px solid ${BRAND.border}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(39, 181, 109, 0.08)',
                    borderColor: BRAND.primary
                  }
                }}
              >
                <Box sx={{
                  color: BRAND.primary,
                  display: 'flex',
                  alignItems: 'center',
                  '& svg': { fontSize: { xs: '1.25rem', md: '1.375rem' } }
                }}>
                  {badge.icon}
                </Box>
                <Typography sx={{
                  fontSize: { xs: '0.9375rem', md: '1rem' }, // 15-16px - LARGER
                  fontWeight: 600,
                  color: BRAND.textSecondary,
                  whiteSpace: 'nowrap'
                }}>
                  {badge.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          STATS BAR - Compact Card Design
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          background: `linear-gradient(180deg, ${BRAND.surface} 0%, rgba(18, 18, 26, 0.98) 100%)`,
          borderTop: `1px solid ${BRAND.border}`,
          borderBottom: `1px solid ${BRAND.border}`,
          py: { xs: 2, md: 2.5 },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: '1px',
            background: `linear-gradient(90deg, transparent, ${BRAND.primary}50, transparent)`
          }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 1, md: 1.5 }}>
            {STATS.map((stat, index) => (
              <Grid item xs={6} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1.5, md: 2 },
                    borderRadius: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: `1px solid ${BRAND.border}`,
                    transition: 'all 0.2s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'rgba(39, 181, 109, 0.05)',
                      borderColor: 'rgba(39, 181, 109, 0.3)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(39, 181, 109, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.25 }}>
                    <Typography
                      component="span"
                      sx={{
                        color: BRAND.primary,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '2.75rem' },
                        fontWeight: 800,
                        fontFamily: "'Inter', sans-serif",
                        letterSpacing: '-0.02em',
                        lineHeight: 1
                      }}
                    >
                      {stat.value}
                    </Typography>
                    {stat.unit && (
                      <Typography
                        component="span"
                        sx={{
                          color: BRAND.primary,
                          fontSize: { xs: '1rem', md: '1.25rem' },
                          fontWeight: 600,
                          opacity: 0.9
                        }}
                      >
                        {stat.unit}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      color: BRAND.textSecondary,
                      fontSize: { xs: '0.875rem', md: '0.9375rem' },
                      fontWeight: 500,
                      mt: 1,
                      letterSpacing: '0.02em'
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          WHAT IS Q? SECTION - Compact Spacing
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        sx={{
          py: { xs: 5, md: 7 },
          px: 2,
          backgroundColor: BRAND.background
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 5 }} alignItems="center">
            <Grid item xs={12} md={6}>
              {/* Section Label with Q branding */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(39, 181, 109, 0.15) 0%, rgba(34, 197, 94, 0.1) 100%)',
                    border: `1px solid rgba(39, 181, 109, 0.3)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 12px rgba(39, 181, 109, 0.15)'
                  }}
                >
                  <Typography
                    sx={{
                      color: BRAND.primary,
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      fontFamily: "'Inter', -apple-system, sans-serif"
                    }}
                  >
                    Q
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: BRAND.primary,
                    fontSize: '1rem', // LARGER label
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  About Q
                </Typography>
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  color: BRAND.text,
                  mb: 2,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.15
                }}
              >
                Meet <Box component="span" sx={{ color: BRAND.primary }}>Q</Box> — Your Trading Analytics Tool
              </Typography>
              <Typography
                sx={{
                  color: BRAND.textSecondary,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.7,
                  mb: 1.5
                }}
              >
                Built by{' '}
                <Box
                  component="a"
                  href="https://www.qforgex.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: BRAND.primary,
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  QFORGEX (Private) Limited
                </Box>
                {' '}— a fintech software solutions provider. Q organizes your personal trading history into dashboards, charts, and pattern breakdowns for self-review.
              </Typography>
              <Typography
                sx={{
                  color: BRAND.textSecondary,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.7,
                  mb: 2.5
                }}
              >
                Unlike generic tools, <Box component="span" sx={{ color: BRAND.primary, fontWeight: 600 }}>Q</Box> understands trading data structure. It processes your history and visualizes patterns—you decide what to do with the information.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { title: 'Transparent Analytics', desc: 'Every breakdown comes with clear data sources—no black box' },
                  { title: 'Tool, Not Advisor', desc: 'Q organizes data. You make all decisions. Full control stays with you.' },
                  { title: 'Fast Processing', desc: 'Optimized data pipeline = sub-second responses' },
                  { title: 'Your Data, Your View', desc: 'Q processes YOUR history—visualizations tailored to your trading' }
                ].map((point, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: { xs: 1.5, md: 2 },
                      backgroundColor: BRAND.surface,
                      borderRadius: '10px',
                      border: `1px solid ${BRAND.border}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: BRAND.primary,
                        backgroundColor: 'rgba(39, 181, 109, 0.03)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <CheckCircleIcon sx={{ color: BRAND.primary, fontSize: 22, mt: 0.25, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1.1875rem', mb: 0.5 }}>
                        {point.title}
                      </Typography>
                      <Typography sx={{ color: BRAND.textMuted, fontSize: '1.0625rem', lineHeight: 1.6 }}>
                        {point.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          FEATURES SECTION - Compact
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        id="features"
        component="section"
        sx={{
          py: { xs: 5, md: 7 },
          px: 2,
          backgroundColor: BRAND.surfaceAlt
        }}
      >
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
            <Typography
              sx={{
                color: BRAND.primary,
                fontSize: '1rem', // 16px - LARGER label
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 2
              }}
            >
              Features
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' }, // LARGER titles
                fontWeight: 700,
                color: BRAND.text,
                mb: 2,
                letterSpacing: '-0.02em',
                lineHeight: 1.15
              }}
            >
              Everything You Need. Nothing You Don't.
            </Typography>
            <Typography
              sx={{
                color: BRAND.textMuted,
                fontSize: { xs: '1.125rem', md: '1.25rem' }, // LARGER subtitle
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Powerful AI-driven tools designed to enhance your trading decisions
            </Typography>
          </Box>

          {/* Feature Cards Grid - Compact 3-column layout */}
          <Grid container spacing={2}>
            {/* First row: 3 cards */}
            {KEY_FEATURES.slice(0, 3).map((feature) => (
              <Grid item xs={12} sm={6} md={4} key={feature.title}>
                <Box
                  sx={{
                    backgroundColor: BRAND.surface,
                    borderRadius: '12px',
                    border: `1px solid ${BRAND.border}`,
                    p: { xs: 2, md: 2.5 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: feature.color,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2), 0 0 20px ${feature.color}20`
                    },
                    '&:hover .feature-top-bar': {
                      transform: 'scaleX(1)'
                    }
                  }}
                >
                  {/* Top accent bar - only shows on hover */}
                  <Box
                    className="feature-top-bar"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: feature.color,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease'
                    }}
                  />

                  {/* Icon */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '10px',
                      backgroundColor: `${feature.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: feature.color,
                      mb: 1.5,
                      '& svg': { fontSize: 24 }
                    }}
                  >
                    {feature.icon}
                  </Box>

                  {/* Title */}
                  <Typography
                    sx={{
                      color: BRAND.text,
                      fontSize: '1.25rem', // 20px - LARGER
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    {feature.title}
                  </Typography>
                  {/* Subtitle */}
                  <Typography
                    sx={{
                      color: feature.color,
                      fontSize: '0.8125rem', // 13px - LARGER
                      fontWeight: 600,
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em'
                    }}
                  >
                    {feature.subtitle}
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      color: BRAND.textSecondary,
                      fontSize: '1.0625rem', // 17px - LARGER
                      lineHeight: 1.6,
                      flex: 1
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
            {/* Second row: 2 cards centered */}
            <Grid item xs={12}>
              <Grid container spacing={2} justifyContent="center">
                {KEY_FEATURES.slice(3).map((feature) => (
                  <Grid item xs={12} sm={6} md={4} key={feature.title}>
                    <Box
                      sx={{
                        backgroundColor: BRAND.surface,
                        borderRadius: '12px',
                        border: `1px solid ${BRAND.border}`,
                        p: { xs: 2, md: 2.5 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          borderColor: feature.color,
                          transform: 'translateY(-3px)',
                          boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2), 0 0 20px ${feature.color}20`
                        },
                        '&:hover .feature-top-bar': {
                          transform: 'scaleX(1)'
                        }
                      }}
                    >
                      {/* Top accent bar - only shows on hover */}
                      <Box
                        className="feature-top-bar"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 2,
                          background: feature.color,
                          transform: 'scaleX(0)',
                          transformOrigin: 'left',
                          transition: 'transform 0.2s ease'
                        }}
                      />

                      {/* Icon */}
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '10px',
                          backgroundColor: `${feature.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: feature.color,
                          mb: 1.5,
                          '& svg': { fontSize: 24 }
                        }}
                      >
                        {feature.icon}
                      </Box>

                      {/* Title */}
                      <Typography
                        sx={{
                          color: BRAND.text,
                          fontSize: '1.25rem', // 20px - LARGER
                          fontWeight: 600,
                          mb: 0.5
                        }}
                      >
                        {feature.title}
                      </Typography>
                      {/* Subtitle */}
                      <Typography
                        sx={{
                          color: feature.color,
                          fontSize: '0.8125rem', // 13px - LARGER
                          fontWeight: 600,
                          mb: 1,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em'
                        }}
                      >
                        {feature.subtitle}
                      </Typography>

                      {/* Description */}
                      <Typography
                        sx={{
                          color: BRAND.textSecondary,
                          fontSize: '1.0625rem', // 17px - LARGER
                          lineHeight: 1.6,
                          flex: 1
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          HOW IT WORKS - 3 STEPS
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        id="how-it-works"
        component="section"
        sx={{
          py: { xs: 5, md: 7 },
          px: 2,
          backgroundColor: BRAND.background
        }}
      >
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
            <Typography
              sx={{
                color: BRAND.primary,
                fontSize: '1rem', // 16px - LARGER label
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 1.5
              }}
            >
              Getting Started
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' }, // LARGER titles
                fontWeight: 700,
                color: BRAND.text,
                mb: 1,
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
            >
              How Q Works
            </Typography>
            <Typography
              sx={{
                color: BRAND.textMuted,
                fontSize: { xs: '1.125rem', md: '1.25rem' } // LARGER subtitle
              }}
            >
              Three steps. Zero complexity.
            </Typography>
          </Box>

          {/* Steps */}
          <Box sx={{ position: 'relative' }}>
            {/* Connection Line - positioned behind circles */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: 56, // Center of 80px circle (40px) + padding (16px from p:2)
                left: 'calc(16.67% + 40px)', // Start from center of first circle
                right: 'calc(16.67% + 40px)', // End at center of last circle
                height: 3,
                background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.primary} 100%)`,
                zIndex: 0,
                borderRadius: 2
              }}
            />

            <Grid container spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
              {HOW_IT_WORKS.map((item) => (
                <Grid item xs={12} md={4} key={item.step}>
                  <Box sx={{ textAlign: 'center', p: 1.5 }}>
                    {/* Step Circle */}
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1.5,
                        boxShadow: `0 4px 16px ${BRAND.glow}`,
                        position: 'relative',
                        color: BRAND.text,
                        zIndex: 2,
                        '& svg': { fontSize: 26 }
                      }}
                    >
                      {item.icon}
                      {/* Step number badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 26,
                          height: 26,
                          borderRadius: '50%',
                          backgroundColor: BRAND.background,
                          border: `2px solid ${BRAND.primary}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`
                        }}
                      >
                        <Typography
                          sx={{
                            color: BRAND.primary,
                            fontSize: '0.8125rem',
                            fontWeight: 800
                          }}
                        >
                          {item.step}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Title */}
                    <Typography
                      sx={{
                        color: BRAND.text,
                        fontSize: '1.375rem', // 22px - LARGER
                        fontWeight: 600,
                        mb: 0.5
                      }}
                    >
                      {item.title}
                    </Typography>

                    {/* Subtitle */}
                    <Typography
                      sx={{
                        color: BRAND.primary,
                        fontSize: '1rem', // 16px - LARGER
                        fontWeight: 600,
                        mb: 1.5
                      }}
                    >
                      {item.subtitle}
                    </Typography>

                    {/* Description */}
                    <Typography
                      sx={{
                        color: BRAND.textSecondary,
                        fontSize: '1rem', // 16px - LARGER
                        lineHeight: 1.6,
                        mb: 1.5,
                        minHeight: { md: 48 }
                      }}
                    >
                      {item.description}
                    </Typography>

                    {/* Time Badge */}
                    <Chip
                      label={`Time: ${item.time}`}
                      sx={{
                        backgroundColor: 'rgba(39, 181, 109, 0.15)',
                        color: BRAND.primary,
                        fontWeight: 600,
                        fontSize: '0.8125rem', // 13px - LARGER
                        px: 0.5,
                        height: 24
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          TESTIMONIALS SECTION
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        sx={{
          py: { xs: 5, md: 7 },
          px: 2,
          backgroundColor: BRAND.surfaceAlt
        }}
      >
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
            <Typography
              sx={{
                color: BRAND.primary,
                fontSize: '1rem', // 16px - LARGER label
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 1.5
              }}
            >
              Testimonials
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' }, // LARGER titles
                fontWeight: 700,
                color: BRAND.text,
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
            >
              Trusted by Traders Worldwide
            </Typography>
          </Box>

          {/* Testimonial Cards */}
          <Grid container spacing={2}>
            {TESTIMONIALS.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    backgroundColor: BRAND.surface,
                    borderRadius: '10px',
                    border: `1px solid ${BRAND.border}`,
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: BRAND.primary,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <FormatQuoteIcon sx={{ color: BRAND.primary, fontSize: 28, mb: 1, transform: 'rotate(180deg)' }} />
                  <Typography
                    sx={{
                      color: BRAND.textSecondary,
                      fontSize: '1.0625rem', // 17px - LARGER
                      lineHeight: 1.6,
                      fontStyle: 'italic',
                      flex: 1,
                      mb: 1.5
                    }}
                  >
                    "{testimonial.quote}"
                  </Typography>
                  <Box>
                    <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1.0625rem' }}>
                      {testimonial.author}
                    </Typography>
                    <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem' }}>
                      {testimonial.role}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          PRICING SECTION
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        id="pricing"
        component="section"
        sx={{
          py: { xs: 5, md: 7 },
          px: 2,
          backgroundColor: BRAND.background
        }}
      >
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
            <Typography
              sx={{
                color: BRAND.primary,
                fontSize: '1rem', // 16px - LARGER label
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 1.5
              }}
            >
              Pricing
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' }, // LARGER titles
                fontWeight: 700,
                color: BRAND.text,
                mb: 1,
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
            >
              Pay Only for What You Use
            </Typography>
            <Typography
              sx={{
                color: BRAND.textMuted,
                fontSize: { xs: '1.125rem', md: '1.25rem' }, // LARGER subtitle
                maxWidth: 560,
                mx: 'auto'
              }}
            >
              Start free with 50 tokens. Top up anytime. No subscriptions required.
            </Typography>

            {/* Token Cost Reference */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: { xs: 1, md: 1.5 },
                mt: 2.5,
                mb: 2
              }}
            >
              {TOKEN_COSTS.map((item) => (
                <Box
                  key={item.action}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '100px',
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    border: `1px solid ${BRAND.border}`
                  }}
                >
                  <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem' }}>
                    {item.action}:
                  </Typography>
                  <Typography sx={{ color: BRAND.primary, fontSize: '0.9375rem', fontWeight: 600 }}>
                    {typeof item.cost === 'number' ? `${item.cost} tokens` : item.cost}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Pricing Cards */}
          <Grid container spacing={1.5} justifyContent="center">
            {PRICING_TIERS.map((tier) => (
              <Grid item xs={6} sm={4} md={2.4} key={tier.name}>
                <Box
                  sx={{
                    backgroundColor: tier.popular ? '#121f16' : BRAND.surface,
                    borderRadius: '12px',
                    border: tier.popular
                      ? `2px solid ${BRAND.primary}`
                      : `1px solid ${BRAND.border}`,
                    p: { xs: 1.5, md: 2 },
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: tier.popular
                        ? `0 8px 24px rgba(39, 181, 109, 0.2)`
                        : `0 8px 20px rgba(0, 0, 0, 0.2)`
                    }
                  }}
                >
                  {/* Popular Badge */}
                  {tier.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: BRAND.primary,
                        color: '#fff',
                        fontSize: '0.5rem', // 8px - refined
                        fontWeight: 700,
                        px: 1.25,
                        py: 0.375,
                        borderRadius: '4px',
                        boxShadow: `0 2px 10px ${BRAND.glow}`,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      BEST VALUE
                    </Box>
                  )}

                  {/* Token Badge */}
                  <Box
                    sx={{
                      textAlign: 'center',
                      mb: 1.5,
                      py: 0.5,
                      px: 1,
                      mx: 'auto',
                      borderRadius: '8px',
                      backgroundColor: tier.price === 0 ? 'rgba(39, 181, 109, 0.1)' : 'rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <Typography
                      sx={{
                        color: tier.price === 0 ? BRAND.primary : BRAND.text,
                        fontSize: { xs: '1.375rem', md: '1.625rem' }, // LARGER token count
                        fontWeight: 700
                      }}
                    >
                      {tier.tokens}
                    </Typography>
                    <Typography
                      sx={{
                        color: BRAND.textMuted,
                        fontSize: '0.8125rem', // 13px - LARGER
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      tokens
                    </Typography>
                  </Box>

                  {/* Plan Name */}
                  <Typography
                    sx={{
                      color: BRAND.text,
                      fontSize: { xs: '1rem', md: '1.125rem' }, // LARGER plan name
                      fontWeight: 600,
                      textAlign: 'center',
                      mb: 0.25
                    }}
                  >
                    {tier.name}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                    {tier.price === 0 ? (
                      <Typography
                        sx={{
                          color: BRAND.primary,
                          fontSize: '1.625rem', // LARGER
                          fontWeight: 700
                        }}
                      >
                        FREE
                      </Typography>
                    ) : (
                      <>
                        <Typography
                          component="span"
                          sx={{
                            color: BRAND.textMuted,
                            fontSize: '1rem', // LARGER
                            fontWeight: 500,
                            verticalAlign: 'top'
                          }}
                        >
                          $
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: BRAND.text,
                            fontSize: '2rem', // LARGER price
                            fontWeight: 700,
                            lineHeight: 1,
                            letterSpacing: '-0.02em'
                          }}
                        >
                          {tier.price}
                        </Typography>
                        {tier.period && (
                          <Typography
                            component="span"
                            sx={{
                              color: BRAND.textMuted,
                              fontSize: '0.875rem' // LARGER period
                            }}
                          >
                            {tier.period === 'one-time' ? '' : tier.period}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  {/* Features */}
                  <Box sx={{ mb: 2.5, flex: 1 }}>
                    {tier.features.map((feature) => (
                      <Box
                        key={feature}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          mb: 0.75
                        }}
                      >
                        <CheckCircleIcon sx={{ color: BRAND.primary, fontSize: 16 }} />
                        <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.9375rem' }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* CTA Button */}
                  <Button
                    fullWidth
                    variant={tier.popular ? 'contained' : 'outlined'}
                    onClick={() => navigate('/q-ai/signup')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1,
                      borderRadius: '8px',
                      fontSize: '1rem', // LARGER button text
                      ...(tier.popular
                        ? {
                            background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
                            boxShadow: `0 0 20px ${BRAND.glow}`,
                            '&:hover': {
                              boxShadow: `0 0 30px rgba(39, 181, 109, 0.5)`
                            }
                          }
                        : {
                            borderColor: BRAND.borderLight,
                            color: BRAND.text,
                            '&:hover': {
                              borderColor: BRAND.primary,
                              backgroundColor: 'rgba(39, 181, 109, 0.1)'
                            }
                          })
                    }}
                  >
                    {tier.cta}
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Money-back Guarantee Badge */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mt: 3,
              py: 1,
              px: 2,
              mx: 'auto',
              maxWidth: 'fit-content',
              borderRadius: '100px',
              backgroundColor: 'rgba(39, 181, 109, 0.08)',
              border: `1px solid rgba(39, 181, 109, 0.2)`
            }}
          >
            <SecurityIcon sx={{ color: BRAND.primary, fontSize: 22 }} />
            <Typography
              sx={{
                color: BRAND.textSecondary,
                fontSize: '1rem', // 16px - LARGER
                fontWeight: 500
              }}
            >
              <Box component="span" sx={{ color: BRAND.primary, fontWeight: 600 }}>
                14-day money-back guarantee
              </Box>
              {' '}— no questions asked
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          FAQ SECTION
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        id="faq"
        component="section"
        sx={{
          py: { xs: 5, md: 7 },
          px: 2,
          backgroundColor: BRAND.surfaceAlt
        }}
      >
        <Container maxWidth="md">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
            <Typography
              sx={{
                color: BRAND.primary,
                fontSize: '1rem', // 16px - LARGER label
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 1.5
              }}
            >
              FAQ
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' }, // LARGER titles
                fontWeight: 700,
                color: BRAND.text,
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
            >
              Frequently Asked Questions
            </Typography>
          </Box>

          {/* FAQ Accordions - Compact */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {FAQ_DATA.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFaq === `panel${index}`}
                onChange={handleFaqChange(`panel${index}`)}
                sx={{
                  backgroundColor: BRAND.surface,
                  borderRadius: '12px !important',
                  border: `1px solid ${BRAND.border}`,
                  boxShadow: 'none',
                  transition: 'all 0.2s ease',
                  '&:before': { display: 'none' },
                  '&:hover': {
                    borderColor: 'rgba(39, 181, 109, 0.3)'
                  },
                  '&.Mui-expanded': {
                    borderColor: BRAND.primary,
                    backgroundColor: 'rgba(39, 181, 109, 0.03)',
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: BRAND.primary, fontSize: 24 }} />}
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    py: { xs: 1, md: 0.75 },
                    minHeight: { xs: 56, md: 60 }, // Refined touch targets
                    '& .MuiAccordionSummary-content': {
                      my: { xs: 1.25, md: 1.5 }
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      marginLeft: 1.5
                    }
                  }}
                >
                  <Typography sx={{
                    color: BRAND.text,
                    fontWeight: 600,
                    fontSize: { xs: '1.0625rem', md: '1.125rem' }, // 17-18px - LARGER
                    lineHeight: 1.4,
                    pr: 1
                  }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.5, pt: 0 }}>
                  <Typography sx={{
                    color: BRAND.textSecondary,
                    lineHeight: 1.7,
                    fontSize: { xs: '1rem', md: '1.0625rem' } // 16-17px - LARGER
                  }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          FINAL CTA SECTION
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        sx={{
          py: { xs: 5, md: 7 },
          px: 2,
          background: `linear-gradient(180deg, ${BRAND.background} 0%, #0D1A14 100%)`,
          position: 'relative'
        }}
      >
        {/* Glow Effect */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '450px',
            height: '220px',
            background: `radial-gradient(ellipse, rgba(39, 181, 109, 0.08) 0%, transparent 60%)`,
            pointerEvents: 'none'
          }}
        />

        <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' }, // LARGER titles
              fontWeight: 700,
              color: BRAND.text,
              mb: 1.5,
              letterSpacing: '-0.02em',
              lineHeight: 1.2
            }}
          >
            Ready to Understand Your Data?
          </Typography>

          <Typography
            sx={{
              color: BRAND.textMuted,
              fontSize: { xs: '1.125rem', md: '1.25rem' }, // LARGER subtitle
              mb: 2.5,
              maxWidth: 440,
              mx: 'auto'
            }}
          >
            Join thousands of traders who use Q to analyze their trading history.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'center' }}>
            <Button
              onClick={() => navigate('/q-ai/signup')}
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '1.0625rem', md: '1.125rem' }, // LARGER buttons
                px: { xs: 3, md: 3.5 },
                py: 1.25,
                borderRadius: '10px',
                boxShadow: `0 0 24px ${BRAND.glow}`,
                '&:hover': {
                  boxShadow: `0 0 40px rgba(39, 181, 109, 0.5)`,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: BRAND.borderLight,
                color: BRAND.text,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem', // LARGER button
                px: 2.5,
                py: 1,
                borderRadius: '10px',
                '&:hover': {
                  borderColor: BRAND.primary,
                  backgroundColor: 'rgba(39, 181, 109, 0.1)'
                }
              }}
            >
              Book a Demo
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="footer"
        sx={{
          py: { xs: 4, md: 5 },
          px: 2,
          backgroundColor: BRAND.surface,
          borderTop: `1px solid ${BRAND.border}`
        }}
      >
        <Container maxWidth="lg">
          {/* Newsletter Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              pb: 3,
              mb: 3,
              borderBottom: `1px solid ${BRAND.border}`
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: BRAND.text,
                  fontSize: { xs: '1.25rem', md: '1.5rem' }, // LARGER
                  fontWeight: 600,
                  mb: 0.5
                }}
              >
                Stay in the loop
              </Typography>
              <Typography
                sx={{
                  color: BRAND.textMuted,
                  fontSize: '1rem' // 16px - LARGER
                }}
              >
                Get trading tips, Q updates, and exclusive content. No spam.
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                width: { xs: '100%', md: 'auto' }
              }}
            >
              <Box
                component="input"
                placeholder="Enter your email"
                sx={{
                  flex: { xs: 1, md: 'none' },
                  width: { md: 260 },
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '8px',
                  border: `1px solid ${BRAND.border}`,
                  backgroundColor: BRAND.background,
                  color: BRAND.text,
                  fontSize: '1rem', // 16px - LARGER
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  '&:focus': {
                    borderColor: BRAND.primary
                  },
                  '&::placeholder': {
                    color: BRAND.textDim
                  }
                }}
              />
              <Button
                variant="contained"
                sx={{
                  background: BRAND.primary,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem', // 16px - LARGER
                  px: 2.5,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: BRAND.primaryLight,
                    boxShadow: 'none'
                  }
                }}
              >
                Subscribe
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {/* Logo & Tagline - Premium Brand Block (+40% prominence) */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: { xs: 2, md: 0 } }}>
                {/* Brand Block: Proportional Logo (v4.0) + Tagline */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                  <QAILogo size="lg" showText />
                  {/* Tagline */}
                  <Typography
                    sx={{
                      color: BRAND.textSecondary,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      ml: '54px' // Align with text after logo icon (42px box + 12px gap)
                    }}
                  >
                    AI-Powered Analytic System
                  </Typography>
                </Box>
                {/* Company Description */}
                <Typography sx={{ color: BRAND.textDim, fontSize: '0.8125rem', mb: 1.5, maxWidth: 280, lineHeight: 1.6 }}>
                  Built by{' '}
                  <Box
                    component="a"
                    href="https://www.qforgex.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: BRAND.primary,
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    QFORGEX (Private) Limited
                  </Box>
                  {' '}— a fintech software solutions provider. Q organizes your personal trading history into dashboards, charts, and pattern breakdowns for self-review.
                </Typography>
                {/* New Text Block with Enlarged Q */}
                <Typography sx={{ color: BRAND.textDim, fontSize: '0.8125rem', mb: 1.5, maxWidth: 280, lineHeight: 1.6 }}>
                  Unlike generic tools, <Box component="span" sx={{ fontSize: '1.1375rem', color: BRAND.primary, fontWeight: 600 }}>Q</Box> understands trading data structure. It processes your history and visualizes patterns—you decide what to do with the information.
                </Typography>
                {/* Social Links */}
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {SOCIAL_LINKS.map((social) => (
                    <IconButton
                      key={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      size="small"
                      sx={{
                        color: BRAND.textMuted,
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        width: 36,
                        height: 36,
                        '&:hover': {
                          color: BRAND.primary,
                          backgroundColor: 'rgba(39, 181, 109, 0.12)'
                        }
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Footer Links - Product */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1rem', mb: 1.5 }}>
                Product
              </Typography>
              {FOOTER_LINKS.product.map((link) => (
                <Typography
                  key={link.label}
                  onClick={() => link.id ? scrollToSection(link.id) : null}
                  sx={{
                    color: BRAND.textMuted,
                    fontSize: '0.9375rem', // 15px - LARGER
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: BRAND.primary }
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Grid>

            {/* Footer Links - Resources */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1rem', mb: 1.5 }}>
                Resources
              </Typography>
              {FOOTER_LINKS.resources.map((link) => (
                <Typography
                  key={link.label}
                  component={link.url?.startsWith('http') ? 'a' : 'span'}
                  href={link.url?.startsWith('http') ? link.url : undefined}
                  target={link.url?.startsWith('http') ? '_blank' : undefined}
                  rel={link.url?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  onClick={() => link.id ? scrollToSection(link.id) : null}
                  sx={{
                    color: BRAND.textMuted,
                    fontSize: '0.9375rem', // 15px - LARGER
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    display: 'block',
                    textDecoration: 'none',
                    '&:hover': { color: BRAND.primary }
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Grid>

            {/* Footer Links - Help Center */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1rem', mb: 1.5 }}>
                Help Center
              </Typography>
              {FOOTER_LINKS.helpCenter.map((link) => (
                <Typography
                  key={link.label}
                  sx={{
                    color: BRAND.textMuted,
                    fontSize: '0.9375rem', // 15px - LARGER
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: BRAND.primary }
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Grid>

            {/* Footer Links - Legal */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography sx={{ color: BRAND.text, fontWeight: 600, fontSize: '1rem', mb: 1.5 }}>
                Legal
              </Typography>
              {FOOTER_LINKS.legal.map((link) => (
                <Typography
                  key={link.label}
                  sx={{
                    color: BRAND.textMuted,
                    fontSize: '0.9375rem', // 15px - LARGER
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: BRAND.primary }
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Grid>
          </Grid>

          {/* Risk Disclaimer Section - Scannable Bulleted Format */}
          <Box
            sx={{
              mt: 3,
              p: { xs: 2, md: 2.5 },
              borderRadius: '12px',
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              border: '2px solid rgba(239, 68, 68, 0.25)'
            }}
          >
            <Typography
              sx={{
                color: '#F87171',
                fontSize: '1.125rem',
                fontWeight: 700,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              ⚠️ Risk Disclosure
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.5,
                color: BRAND.textSecondary,
                fontSize: '0.9375rem',
                lineHeight: 2,
                '& li': { mb: 0.5 },
                '& li::marker': { color: '#F87171' }
              }}
            >
              <li>
                <Box component="span" sx={{ color: BRAND.text, fontWeight: 700 }}>Q.ai</Box> is an educational AI tool — NOT a broker, advisor, or signal service
              </li>
              <li>We do NOT execute trades, manage funds, or guarantee profits</li>
              <li>Trading involves significant risk — you may lose capital</li>
              <li>AI responses may contain errors — always verify independently</li>
              <li>Past performance ≠ future results</li>
              <li>
                <Box component="span" sx={{ color: '#F87171', fontWeight: 500 }}>
                  Service not available in the United States due to regulatory requirements.
                </Box>
              </li>
            </Box>
          </Box>

          {/* Copyright */}
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: `1px solid ${BRAND.border}`,
              textAlign: 'center'
            }}
          >
            <Typography sx={{ color: BRAND.textDim, fontSize: '0.9375rem' }}>
              © 2025 QFORGEX (Private) Limited. All rights reserved.
            </Typography>
            <Typography sx={{ color: BRAND.textDim, fontSize: '0.8125rem', mt: 1.5, maxWidth: 600, mx: 'auto', lineHeight: 1.5 }}>
              Q is a data analytics tool. It does not provide trading signals, financial advice, or investment recommendations.
              All trading decisions are made solely by the user. Past performance does not guarantee future results.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Global Animations */}
      <style>
        {`
          @keyframes heroGlow {
            0%, 100% { opacity: 0.8; transform: translateX(-50%) scale(1); }
            50% { opacity: 1; transform: translateX(-50%) scale(1.05); }
          }
        `}
      </style>
    </Box>
  );
};

export default QAILandingPage;
